import { faEdit, faRemove } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { createRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import ModalComponent from "src/core/components/modal/modal.component";
import FormBuilderComponent, {
  FormConfig,
} from "src/shared/form-builder/form-builder.component";
import { safeAtob } from "src/shared/helpers/enode-decode-string";
import {
  addEditFloorWithBlockCode,
  deleteBlockWithBlockToFloorId,
  GetAllFloorNameByBlockCode,
  getBlockToFloorById,
} from "src/store/slices/dashboard.slice";
import * as yup from "yup";
import faultStatus from "../../../../assets/images/exclamation-circle.png";
import lightOff from "./canvas/images/off-initial.png";
import lightOn from "./canvas/images/on-online-filled.png";
import { faWifiStrong } from "@fortawesome/free-solid-svg-icons";

const StatusDisplay = ({
  item,
  isFloorPlanView = false,
  setState,
  resetComponent,
  navigateToUrl,
}: any) => {
  const params: any = useParams();
  const navigate = useNavigate();

  const blockCode: any = params.blockCode ? safeAtob(params.blockCode) : null;

  const formRef = createRef();
  const dispatch = useDispatch();
  const [updateForm, setUpdateForm] = useState(0);
  const [editLevelModal, setEditLevelModal] = useState(false);

  const [initialValues, setInitialValues] = useState<any>({
    blockCode: blockCode,
    FloorCode: 0,
  });

  const onSubmitLevelModal = () => {
    const form: any = formRef.current;

    if (form) {
      form.handleSubmit();
      const { FloorCode } = form.values;

      if (!FloorCode || FloorCode?.length > 100) {
        return;
      }

      dispatch(
        addEditFloorWithBlockCode({ blockCode, Id: item?.Id, FloorCode })
      ).then((res: any) => {
        const currentUrl = new URL(window.location.href);

        // Update the param (L10 in your case)
        const updatedPathname = currentUrl.pathname.replace(
          /\/floorplan\/[^\/]+/,
          `/floorplan/${encodeURIComponent(FloorCode)}`
        );

        // Navigate to the updated URL with the updated param
        navigate(`${updatedPathname}${currentUrl.search}`);
        setEditLevelModal(false);

        if (res.payload.status === 201) {
          toast.success(res.payload.message, {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnFocusLoss: true,
            draggable: true,
            pauseOnHover: true,
          });
        }

        resetComponent();
      });
    }
  };

  const editLevelModalActionConfig: any = [
    {
      label: "Save",
      event: onSubmitLevelModal,
      variant: "primary",
    },
  ];

  const [isDeleteFloor, setIsDeleteFloor] = useState(false);

  const onDeleteFloor = () => {
    dispatch(deleteBlockWithBlockToFloorId({ BlockToFloorId: item?.Id })).then(
      (res: any) => {
        if (res.payload.status === 404) {
          toast.warning(res.payload.message, {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnFocusLoss: true,
            draggable: true,
            pauseOnHover: true,
          });

          setIsDeleteFloor(false);
        } else {
          toast.success(res.payload.message, {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnFocusLoss: true,
            draggable: true,
            pauseOnHover: true,
          });

          dispatch(
            GetAllFloorNameByBlockCode({
              blockCode,
              PageNumber: 1,
              PageSize: 15,
            })
          ).then((res: any) => {
            const firstFloor = res?.payload?.data?.at(0)?.FloorCode;

            if (firstFloor) {
              navigateToUrl(`${encodeURIComponent(firstFloor)}?update=true`);
              resetComponent();
            } else {
              navigateToUrl(`/dashboard/${btoa(blockCode)}/floorplan`);
              resetComponent();
            }

            setIsDeleteFloor(false);
          });
        }
      }
    );
  };

  const modalActionConfig: any = [
    {
      label: "Yes",
      event: onDeleteFloor,
      variant: "primary",
    },
  ];

  const validationSchema = yup.object().shape({
    FloorCode: yup
      .string()
      .required("FloorCode is required")
      .max(100, "FloorCode must be at most 100 characters"),
  });

  const formConfig: FormConfig = {
    controls: [
      {
        name: "blockCode",
        label: "Block Code",
        placeholder: "",
        type: "text",
        required: true,
        readOnly: true,
        disabled: true,
      },
      {
        name: "FloorCode",
        label: "Floor",
        placeholder: "",
        type: "text",
        required: true,
      },
    ],
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          fontSize: "15px",
        }}
      >
        <div style={{ display: "flex" }}>
          <div
            className="d-flex align-items-center"
            style={{ color: "#08875D" }}
          >
            <img
              className="h5 ms-1"
              src={lightOn}
              alt="Online Status"
              title="Total Light ON"
              width="25px"
              height="25px"
            />
            <small
              style={{
                color: "08875D",
                display: "block",
                minWidth: "26px",
                marginTop: "12px",
              }}
              className="fs-6"
            >
              {item?.OnlineCount || 0}
            </small>
          </div>
        </div>
        <div style={{ display: "flex" }}>
          <div
            className="d-flex align-items-center"
            style={{
              color: "#B36500",
              display: "flex",
            }}
          >
            <div style={{ position: "relative" }}>
              <img
                className="h5"
                src={lightOff}
                alt="Offline Status"
                title="Total Light OFF"
                width="25px"
                height="25px"
              />
            </div>
            <small
              style={{
                color: "#B36500",
                marginTop: "12px",
                display: "block",
                minWidth: "26px",
              }}
              className="fs-6"
            >
              {item?.OfflineCount || 0}
            </small>
          </div>
        </div>
        {/* <div style={{ display: "flex" }}>
          <div className="d-flex align-items-center" style={{ color: "red" }}>
            <img
              className="h5"
              src={faultStatus}
              alt="Fault Status"
              title="Total Fault"
              width="25px"
              height="25px"
            />
            <small
              style={{
                color: "red",
                display: "block",
                minWidth: "26px",
                marginTop: "12px",
              }}
              className="fs-6"
            >
              {item?.FaultCount || 0}
            </small>
          </div>
        </div> */}

        { item?.OfflineFaultCount !== undefined &&
          <div style={{ color: "#B36500", display: "flex" }}>
            <div style={{ position: "relative" }}>
              <FontAwesomeIcon
                icon={faWifiStrong}
                className="h5 ms-2"
                title="Offline"
              />

              <div
                style={{
                  position: "absolute",
                  top: "0px",
                  left: "18.3px",
                  fontSize: "16px",
                }}
              >
                !
              </div>
            </div>
            <small
              style={{
                color: "#B36500",
                marginTop: "12px",
                display: "block",
                minWidth: "26px",
              }}
              className="fs-6"
            >
              {item?.OfflineFaultCount || 0}
            </small>
          </div>
        }
        <>
          {!isFloorPlanView && (
            <>
              <FontAwesomeIcon
                onClick={() => {
                  setEditLevelModal(true);

                  dispatch(getBlockToFloorById({ Id: item?.Id })).then(
                    (res: any) => {
                      setInitialValues((prev: any) => ({
                        ...prev,
                        FloorCode: res?.payload?.data,
                      }));
                      setUpdateForm((prev: any) => prev + 1);
                    }
                  );
                }}
                icon={faEdit}
                className="fs-7 ms-2"
                style={{ width: "20px", height: "20px" }}
                title="Edit"
              />
              <FontAwesomeIcon
                onClick={() => {
                  setIsDeleteFloor(true);
                }}
                icon={faRemove}
                className="fs-7 ms-2"
                style={{ width: "20px", height: "20px" }}
                title="Remove"
              />
            </>
          )}
        </>
      </div>

      {editLevelModal && (
        <ModalComponent
          headerTitle="Edit Floor Level"
          showModal={editLevelModal}
          handleClose={() => {
            setEditLevelModal(false);
            setUpdateForm((prev: any) => prev + 1);
          }}
          actionConfig={editLevelModalActionConfig}
        >
          <FormBuilderComponent
            renderCount={updateForm}
            formRef={formRef}
            initialValues={initialValues}
            validation={validationSchema}
            formConfig={formConfig}
          />
        </ModalComponent>
      )}

      {isDeleteFloor && (
        <ModalComponent
          isConformationModal={isDeleteFloor}
          showModal={isDeleteFloor}
          handleClose={() => setIsDeleteFloor(false)}
          headerTitle="Delete Floor"
          actionConfig={modalActionConfig}
        >
          <h5 className="text-center">
            Are you sure to <b className="text-danger">Delete</b>{" "}
            {item?.FloorCode}?
          </h5>
        </ModalComponent>
      )}
    </>
  );
};

export default StatusDisplay;
